import { useState, useCallback, useEffect, useRef } from 'react';
import { getTaskList, getFavoriteTasks, deleteTask, updateFavorite, updateTaskTitle, Task } from '../services/task';
import { getTasksByFolder } from '../services/folder';
import { useLanguage } from '../i18nContext';

interface UseTasksOptions {
  activeTab: string;
  onTaskCompleted?: (title: string) => void;
  onTaskFailed?: (title: string) => void;
}

export function useTasks({ activeTab, onTaskCompleted, onTaskFailed }: UseTasksOptions) {
  const { language } = useLanguage();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchTasks = useCallback(async (showLoading = true) => {
    if (showLoading) setLoading(true);
    setError(null);
    try {
      let response;
      if (activeTab === 'all') {
        response = await getTaskList();
      } else if (activeTab === 'favorites') {
        response = await getFavoriteTasks();
      } else if (activeTab.startsWith('folder_')) {
        const folderId = activeTab.replace('folder_', '');
        response = await getTasksByFolder(folderId);
      } else {
        response = await getTaskList();
      }
      setTasks(response || []);
    } catch (e: any) {
      console.error('Failed to fetch tasks:', e);
      setError(e.message || (language === 'zh' ? '获取数据失败' : 'Failed to fetch data'));
      setTasks([]);
    } finally {
      if (showLoading) setLoading(false);
    }
  }, [activeTab, language]);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  useEffect(() => {
    const hasProcessingTasks = tasks.some(task =>
      task.status !== 'completed' && task.status !== 'failed'
    );
    if (!hasProcessingTasks) return;
    const pollInterval = setInterval(() => {
      fetchTasks(false);
    }, 3000);
    return () => clearInterval(pollInterval);
  }, [tasks, fetchTasks]);

  const prevTaskStatusRef = useRef<Record<string, string>>({});
  useEffect(() => {
    const prev = prevTaskStatusRef.current;
    tasks.forEach(task => {
      const taskId = String(task.taskId);
      const oldStatus = prev[taskId];
      if (oldStatus && oldStatus !== 'completed' && oldStatus !== 'failed') {
        const title = task.title || (language === 'zh' ? '未命名任务' : 'Untitled Task');
        if (task.status === 'completed') {
          onTaskCompleted?.(title);
        } else if (task.status === 'failed') {
          onTaskFailed?.(title);
        }
      }
    });
    const newMap: Record<string, string> = {};
    tasks.forEach(task => { newMap[String(task.taskId)] = task.status; });
    prevTaskStatusRef.current = newMap;
  }, [tasks, language, onTaskCompleted, onTaskFailed]);

  return {
    tasks,
    setTasks,
    loading,
    error,
    fetchTasks
  };
}
